package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetDestructiveTextButton
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone

/**
 * SPS §12.3's invite link.
 *
 * The SDS designs no screen for this -- §14 covers the group card and the group identity block and
 * stops there -- so the layout below is a DECISION built from components that already exist: the
 * profile QR sheet's selectable link block and copy button, the design system's buttons, and the
 * standard skeleton / empty / error / first-frame states §25 asks every surface for.
 *
 *
 * WHY NOTHING LOADS WHEN THIS SCREEN OPENS
 *
 * There is no operation that reads a group's invite link. `createOrRotateInviteLink` is the only
 * one that returns a link and it is a POST that, for an owner or an admin, REPLACES whatever the
 * group had. So a screen that fetched on entry would destroy the group's link every time somebody
 * opened it to look at it -- which is why the first frame here is an explanation and a button, not
 * a spinner. `GroupInviteViewModel` documents the rule; this file is what it looks like.
 *
 *
 * THE LINK IS A CREDENTIAL, SO
 *
 *  * Copying and replacing sit next to each other, at the same width and the same reach. Replacing
 *    is the only way to invalidate a link that has gone somewhere it should not have -- v1.0.0 has
 *    no revoke, no delete and no disable, whatever STA §9.2 says -- so it cannot be the thing
 *    hidden behind an overflow.
 *  * The moment a replacement is confirmed the token leaves the screen. Not greyed, not faded:
 *    gone. Anything still on screen after that point would be a credential the user may already
 *    have destroyed.
 *  * The confirmation names the consequence people actually have -- a link already shared in three
 *    other places -- rather than asking whether they are sure.
 *  * No expiry is ever claimed. `InviteLink` carries `url`, `token` and `created_at`, and nothing
 *    else; the server stores an expiry that nothing sets, so "expires in 7 days" would be invented.
 */
@Composable
fun GroupInviteScreen(
    state: GroupInviteUiState,
    onBack: () -> Unit,
    /** `createOrRotateInviteLink`. Only ever from an explicit tap -- see the file header. */
    onCreateOrRotate: () -> Unit,
    modifier: Modifier = Modifier,
    /** Re-reads the GROUP, never the link. Safe to press repeatedly. */
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    val colors = SwetgramThemeTokens.colors
    var confirming by remember { mutableStateOf(false) }
    // Reset whenever the token changes, so "Link copied" can never describe the previous link.
    var copied by remember(state.link?.token) { mutableStateOf(false) }
    val clipboard = LocalClipboardManager.current
    val group = state.group

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        SwetTopAppBar(title = "Invite link", onBack = onBack)

        // Over the content, never in place of it: a failed group read must not take away a link
        // this session was already handed, which is the only copy of it that exists.
        if (state.error != null) {
            SwetErrorBanner(message = state.error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            state.loading -> InviteSkeleton()

            group == null -> SwetEmptyState(
                title = "Group unavailable.",
                body = "We could not load this group.",
                actionText = onRetry?.let { "Try again" },
                onAction = onRetry,
            )

            else -> Column(
                modifier = Modifier
                    .fillMaxSize()
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin),
            ) {
                Spacer(Modifier.height(Space.three))

                // What the link DOES, which differs entirely between a public and a private group
                // and is the thing somebody about to paste it into a large channel needs first.
                Text(
                    text = inviteLinkAudienceLine(group.visibility),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.textPrimary,
                )

                Spacer(Modifier.height(Space.two))

                // The history rule, stated wherever somebody joins or is admitted, because it is
                // the one consequence nobody expects: DECISIONS §7 sets `min_visible_seq` to the
                // conversation's current position, so arriving is not being caught up.
                Text(
                    text = "People who join this way see messages sent after they arrive, not the " +
                        "group's earlier history.",
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textSecondary,
                )

                Spacer(Modifier.height(Space.three))

                val link = state.link
                if (link != null) {
                    // Selectable, exactly as the profile QR sheet's payload is: the link is the
                    // half of this that gets pasted, and a copy button is not the only way people
                    // move text off a screen.
                    SelectionContainer {
                        Text(
                            text = link.url,
                            style = MaterialTheme.typography.bodySmall,
                            color = colors.textPrimary,
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(Radius.sm))
                                .background(colors.card)
                                .padding(Space.two),
                        )
                    }

                    // Omitted rather than faked when the date could not be read -- the link is
                    // still the only copy of the token and is worth showing without it.
                    link.createdAt?.let {
                        Spacer(Modifier.height(Space.one))
                        Text(
                            text = "Created on ${LocalDay.of(it, zone).render()}",
                            style = MaterialTheme.typography.labelMedium,
                            color = colors.textSecondary,
                        )
                    }

                    Spacer(Modifier.height(Space.two))

                    // Clipboard rather than a system share sheet, for the reason the profile QR
                    // sheet gives: a share sheet needs a platform launcher on both Android and iOS
                    // and `:shared` has no such abstraction. This one is Compose's own.
                    SwetPrimaryButton(
                        text = if (copied) "Link copied" else "Copy link",
                        onClick = {
                            clipboard.setText(AnnotatedString(link.url))
                            copied = true
                        },
                    )
                } else if (state.uncertain) {
                    // The honest version of a failed rotation. The request is never retried, so a
                    // timeout may have committed a new token this client never received: the old
                    // link may already be dead. Saying "something went wrong" would imply nothing
                    // happened, and that is the one thing nobody can promise here.
                    Text(
                        text = "We could not tell whether the link changed. The old link may " +
                            "already have stopped working. Create a new one to be sure which " +
                            "link is live.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textPrimary,
                    )
                    Spacer(Modifier.height(Space.two))
                }

                Spacer(Modifier.height(Space.one))

                InviteAction(
                    action = state.action,
                    busy = state.busy,
                    onAct = { confirming = true },
                    onActWithoutConfirming = onCreateOrRotate,
                )

                // The one state with nothing on it: no link, and no control to get one. It is only
                // reachable for somebody who may not share this group's invitation at all -- a
                // private group's plain member -- and the group screen already hides the entry, so
                // this is what a back-stack or a stale screen lands on. A screen that simply ends
                // here would read as broken, so it says which it is.
                if (state.action == InviteLinkAction.NONE && link == null) {
                    Text(
                        text = "Only this group's owner and admins can share its invite link.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textSecondary,
                    )
                }

                Spacer(Modifier.height(Space.six))
            }
        }
    }

    // SDS §11.1's confirmation for a destructive action. The body is the caution attached to the
    // control, so the sentence in the dialog is the same claim the screen made under the button --
    // one of them being softer than the other is how a warning stops being read.
    if (confirming) {
        val caution = inviteLinkCaution(state.action)
        ProfileConfirmDialog(
            title = when (state.action) {
                InviteLinkAction.REPLACE -> "Replace this invite link?"
                else -> "Create a new invite link?"
            },
            body = caution.orEmpty(),
            confirmText = when (state.action) {
                InviteLinkAction.REPLACE -> "Replace link"
                else -> "Create link"
            },
            onConfirm = {
                confirming = false
                onCreateOrRotate()
            },
            onDismiss = { confirming = false },
            busy = state.busy,
        )
    }
}

/**
 * The single control, and the sentence that has to be true of it before it is pressed.
 *
 * SHOW gets a primary button and no warning, because for a public group's plain member the call
 * writes nothing. The other two get the warning first and the confirmation after, and REPLACE is
 * drawn destructively because by then the client knows for certain that a live link is about to
 * stop working.
 */
@Composable
private fun InviteAction(
    action: InviteLinkAction,
    busy: Boolean,
    /** Opens the confirmation. */
    onAct: () -> Unit,
    /** Goes straight to the call, for the one action that destroys nothing. */
    onActWithoutConfirming: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    inviteLinkCaution(action)?.let { caution ->
        Text(
            text = caution,
            style = MaterialTheme.typography.bodySmall,
            color = colors.textSecondary,
        )
        Spacer(Modifier.height(Space.two))
    }

    when (action) {
        InviteLinkAction.SHOW -> SwetPrimaryButton(
            text = "Show invite link",
            onClick = onActWithoutConfirming,
            loading = busy,
        )

        InviteLinkAction.CREATE_OR_REPLACE -> SwetPrimaryButton(
            text = "Create invite link",
            onClick = onAct,
            loading = busy,
        )

        // A text button rather than a filled destructive one, and it sits directly under Copy at
        // the same width. SDS §11.1: destructive-secondary. The point is equal REACH, not equal
        // emphasis -- an admin who needs to kill a leaked link must not have to hunt for this.
        InviteLinkAction.REPLACE -> SwetDestructiveTextButton(
            text = "Replace link",
            onClick = onAct,
            enabled = !busy,
        )

        // Nothing to press. Only reachable for somebody who may not share this group's invitation
        // at all, or for a public group's plain member who already has the link on screen -- the
        // sentence below says which, without offering a control that would 403.
        InviteLinkAction.NONE -> Unit
    }
}

/**
 * The first frame, while the GROUP is on its way.
 *
 * Deliberately short: nothing here is a placeholder for a link, because no link is being fetched.
 * It stands in for the two sentences and the button, which is all this screen has before somebody
 * presses something.
 */
@Composable
private fun InviteSkeleton() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = Space.screenMargin),
    ) {
        Spacer(Modifier.height(Space.three))
        SwetSkeleton(Modifier.fillMaxWidth().height(Space.two))
        Spacer(Modifier.height(Space.one))
        // Unequal widths on purpose: two bars of the same length read as a rendering fault.
        SwetSkeleton(Modifier.fillMaxWidth(0.62f).height(Space.two))
        Spacer(Modifier.height(Space.four))
        SwetSkeleton(
            Modifier.fillMaxWidth().height(TouchTarget.buttonHeight),
            shape = RoundedCornerShape(Radius.md),
        )
    }
}
